import React, {ReactElement, ReactText} from 'react';
import {useIntl} from 'react-intl';
import {Field, FieldProps} from 'formik';

import {ObjectOfAny} from '@fo/shared-types';
import {FormikField} from '@fo/designSystem/Fields/types';
import Currency from './Currency';
import getRequiredMessage from '../validationMessages/getRequiredMessage';
import getNumberMessage from '../validationMessages/getNumberMessage';

export interface Props<FormValues> extends Omit<FormikField, 'name'> {
  maximumValue?: number;
  minimumValue?: number;
  name: keyof FormValues;
  testId?: string;
  onChange?: (name: string, value: ReactText) => void;
  onBlur?: () => void;
  maxLength?: number;
  tooltip?: string;
  valueOverride?: string;
  inputClassName?: string;
  assistiveClassName?: string;
  inputLabelClassName?: string;
  onBlurValidate?: boolean;
}

function CurrencyWithFormikField<FormValues>({
  name,
  validate = () => {
    /* noop */
  },
  placeholder,
  label,
  assistiveMessage,
  required,
  isDisabled,
  warningMessage,
  errorMessages,
  minimumValue,
  maximumValue,
  showOptionalMessage,
  testId,
  tooltip,
  onChange = () => {
    /* noop */
  },
  onBlur = () => {
    /* noop */
  },
  onBlurValidate = false,
  useCustom,
  maxLength,
  className,
  isReadOnly,
  valueOverride,
  inputClassName,
  inputLabelClassName,
  assistiveClassName,
}: Readonly<Props<FormValues>>): ReactElement {
  const {formatMessage} = useIntl();
  const validateFieldProp = (value: string) => {
    const requiredMessage = errorMessages?.required || '';
    const requiredErrorMessage = getRequiredMessage(
      formatMessage,
      !!useCustom,
      name,
      value,
      !!required,
      requiredMessage
    );
    if (requiredErrorMessage) {
      return requiredErrorMessage;
    }

    // @todo: remove legacy validation
    const legacyMessages = {
      number: (values: ObjectOfAny) => formatMessage({id: 'phoenix.errors.number.validFormat'}, values),
      minMaxNumber: (values: ObjectOfAny) => formatMessage({id: 'phoenix.errors.number.minAndMax'}, values),
      minNumber: (values: ObjectOfAny) => formatMessage({id: 'phoenix.errors.number.min'}, values),
      maxNumber: (values: ObjectOfAny) => formatMessage({id: 'phoenix.errors.number.max'}, values),
    };
    const numberValues = {minimumValue, maximumValue, value: Number(value)};
    const numberErrorMessage = getNumberMessage(
      formatMessage,
      numberValues,
      useCustom ? errorMessages : legacyMessages
    );
    if (numberErrorMessage) {
      return numberErrorMessage;
    }

    // custom validator
    if (value.length > 0 || typeof value === 'number') {
      return validate(value);
    }

    return undefined;
  };

  return (
    <Field name={name} validate={validateFieldProp}>
      {({form, field, meta}: FieldProps) => (
        <Currency
          isReadOnly={isReadOnly}
          assistiveMessage={assistiveMessage}
          placeholder={placeholder}
          label={label}
          errorMessage={meta.error}
          name={field.name}
          value={valueOverride || field.value}
          tooltip={tooltip}
          onChange={(_, value) => {
            form.setFieldValue(field.name, value);
            onChange(field.name, value);
          }}
          onBlur={(e) => {
            field.onBlur(e);
            if (onBlurValidate) {
              form.validateField(field.name);
            }
            if (onBlur) {
              onBlur();
            }
          }}
          warningMessage={warningMessage}
          minimumValue={minimumValue}
          maximumValue={maximumValue}
          showOptionalMessage={showOptionalMessage}
          isDisabled={isDisabled}
          testId={testId}
          maxLength={maxLength}
          className={className}
          inputClassName={inputClassName}
          inputLabelClassName={inputLabelClassName}
          assistiveClassName={assistiveClassName}
        />
      )}
    </Field>
  );
}

export default CurrencyWithFormikField;
